import { Clock } from 'lucide-react';

interface MetricsDashboardProps {
  selectedCount: number;
}

interface CircularMetricProps {
  value: number;
  unit: string;
  title: string;
  description: string;
  progress: number;
  colorClass: string;
}

const CircularMetric: React.FC<CircularMetricProps> = ({
  value,
  unit,
  title,
  description,
  progress,
  colorClass,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center border-2 border-slate-200 dark:border-slate-700">
    <div className="relative inline-flex items-center justify-center mb-4">
      <svg className="w-40 h-40 transform -rotate-90">
        <circle
          cx="80"
          cy="80"
          r="70"
          stroke="currentColor"
          strokeWidth="12"
          fill="none"
          className="text-slate-200 dark:text-slate-700"
        />
        <circle
          cx="80"
          cy="80"
          r="70"
          stroke="currentColor"
          strokeWidth="12"
          fill="none"
          strokeDasharray={`${2 * Math.PI * 70}`}
          strokeDashoffset={`${2 * Math.PI * 70 * (1 - progress)}`}
          className={`${colorClass} transition-all duration-1000`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-slate-900 dark:text-slate-50">{value}</span>
        <span className="text-sm text-slate-500 dark:text-slate-400">{unit}</span>
      </div>
    </div>
    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">{title}</h3>
    <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
  </div>
);

export const MetricsDashboard: React.FC<MetricsDashboardProps> = ({ selectedCount }) => {
  const monthlyMaintenanceHours = Math.round(selectedCount * 6);

  return (
    <div className="mb-16">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-slate-900 dark:text-slate-50 mb-3">
          System Performance <span className="text-blue-500">Metrics</span>
        </h2>
        <p className="text-slate-600 dark:text-slate-400 text-lg">
          See how fixing these issues will improve your system and save you time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <CircularMetric
          value={Math.round(selectedCount * 12)}
          unit="hrs"
          title="Setup Time"
          description="Hours needed for initial implementation"
          progress={0.75}
          colorClass="text-blue-500 dark:text-blue-400"
        />
        <CircularMetric
          value={Math.round(selectedCount * 6)}
          unit="hrs"
          title="Monthly Maintenance"
          description="Hours saved monthly with automation"
          progress={0.5}
          colorClass="text-orange-500 dark:text-orange-400"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 bg-linear-to-r from-blue-50 to-teal-50 dark:from-blue-900/20 dark:to-teal-900/20 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-blue-500 text-white rounded-full p-3">
              <Clock className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                Save {monthlyMaintenanceHours} hours/month in developer time
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Based on automated fixes for {selectedCount} recommendation
                {selectedCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="text-center md:text-right">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {monthlyMaintenanceHours * 12} hrs/year
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Time savings with automation
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
